import { useCallback, useEffect, useRef, useState } from "react";
import { api, errMsg, itDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Camera, Images, FirstAidKit } from "@phosphor-icons/react";

export default function MySickness() {
  const today = new Date().toISOString().slice(0, 10);
  const [certs, setCerts] = useState([]);
  const [cert, setCert] = useState({ date_from: today, date_to: today, notes: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const load = useCallback(() => {
    api.get("/certificates").then((r) => setCerts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const list = [...(e.target.files || [])];
    if (!list.length) return;
    setBusy(true);
    const fd = new FormData();
    list.forEach((f) => fd.append("files", f));
    fd.append("date_from", cert.date_from);
    fd.append("date_to", cert.date_to);
    fd.append("notes", cert.notes || "");
    try {
      await api.post("/certificates", fd);
      toast.success(list.length > 1
        ? `Certificato inviato al responsabile (${list.length} pagine)`
        : "Certificato inviato al responsabile");
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const declareSickness = async () => {
    setBusy(true);
    try {
      await api.post("/requests", {
        type: "MALATTIA", date_from: cert.date_from, date_to: cert.date_to,
        notes: cert.notes || "Malattia comunicata dal dipendente (certificato non allegato)",
      });
      toast.success("Malattia comunicata al responsabile");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="my-sickness-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Malattia</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Comunica i giorni di malattia. Il certificato è facoltativo: puoi inviarlo subito oppure più tardi.
        </p>
      </div>

      <div className="rounded-sm border border-border bg-card p-4">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Dal</Label>
            <Input type="date" value={cert.date_from} onChange={(e) => setCert({ ...cert, date_from: e.target.value })}
              className="h-9 rounded-sm mt-1" data-testid="cert-from" />
          </div>
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Al</Label>
            <Input type="date" value={cert.date_to} onChange={(e) => setCert({ ...cert, date_to: e.target.value })}
              className="h-9 rounded-sm mt-1" data-testid="cert-to" />
          </div>
        </div>
        <div className="mt-2">
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Note</Label>
          <Input value={cert.notes} onChange={(e) => setCert({ ...cert, notes: e.target.value })}
            className="h-9 rounded-sm mt-1" placeholder="Numero protocollo, medico..." data-testid="cert-notes" />
        </div>
        <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple
          onChange={upload} className="hidden" data-testid="cert-file-input" />
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" multiple
          onChange={upload} className="hidden" data-testid="cert-camera-input" />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
          <Button className="rounded-sm font-semibold" disabled={busy}
            onClick={declareSickness} data-testid="sick-declare-btn">
            <FirstAidKit size={16} className="mr-1.5" /> Comunica malattia
          </Button>
          <Button variant="outline" className="rounded-sm font-semibold" disabled={busy}
            onClick={() => cameraRef.current?.click()} data-testid="cert-camera-btn">
            <Camera size={16} className="mr-1.5" /> {busy ? "Invio..." : "Scatta foto"}
          </Button>
          <Button variant="outline" className="rounded-sm font-semibold" disabled={busy}
            onClick={() => fileRef.current?.click()} data-testid="cert-upload-btn">
            <Images size={16} className="mr-1.5" /> {busy ? "Invio..." : "Galleria o file"}
          </Button>
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">
          Puoi scattare la foto del certificato, scegliere una o più immagini dalla galleria
          oppure allegare un PDF: se il certificato ha più pagine selezionale tutte insieme.
        </p>
      </div>

      <div className="rounded-sm border border-border bg-card p-4 space-y-1.5">
        <h2 className="font-head font-semibold text-base">Certificati inviati</h2>
        {certs.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded-sm border border-border px-2.5 py-1.5 text-xs"
            data-testid={`my-cert-${c.id}`}>
            <span className="tabular-nums">{itDate(c.date_from)} → {itDate(c.date_to)}</span>
            <span className="font-semibold">{c.status.replace("_", " ")}</span>
          </div>
        ))}
        {certs.length === 0 && <p className="text-xs text-muted-foreground">Nessun certificato inviato.</p>}
      </div>
    </div>
  );
}
